import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { auth, onAuthStateChanged } from "../components/firebase";
import { getUser } from "../integrations/APIClient";

export const UserContext = createContext({
  user: null,
  firebaseUser: null,
  loading: true,
  profileLoading: false,
  refresh: async () => null,
});

export function UserContextProvider({ children }) {
  const [user, setUser] = useState(null);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  const refresh = useCallback(async () => {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      setUser(null);
      setFirebaseUser(null);
      setLoading(false);
      setProfileLoading(false);
      return null;
    }

    setFirebaseUser(currentUser);
    setProfileLoading(true);

    try {
      const backendUser = await getUser();
      setUser(backendUser);
      return backendUser;
    } catch (error) {
      console.error("Failed to fetch backend user:", error);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setFirebaseUser(currentUser || null);
      setLoading(false);

      if (currentUser) {
        refresh();
      } else {
        setUser(null);
        setLoading(false);
        setProfileLoading(false);
      }
    });

    return () => unsubscribe();
  }, [refresh]);

  const value = useMemo(
    () => ({
      user,
      firebaseUser,
      loading,
      profileLoading,
      refresh,
    }),
    [firebaseUser, loading, profileLoading, refresh, user]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}
